import { useEffect, useMemo, useRef, useState } from 'react';
import { Scale, X } from 'lucide-react';
import Button from '../../components/Button';
import { formatCurrency } from '../../data/mockData';
import {
  formatWeightKg, gramsToKg, lineAmount, qtyOnStep, quantizeKg, stepLabel,
} from '../../utils/soldBy';
import styles from './WeighSheetModal.module.css';

const QUICK_WEIGHTS = [
  { label: '250 g', kg: 0.25 },
  { label: '500 g', kg: 0.5 },
  { label: '1 kg', kg: 1 },
];

function parseKgInput(raw) {
  const cleaned = String(raw || '').trim().replace(',', '.');
  if (!cleaned) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n <= 0) return null;
  return quantizeKg(n);
}

export default function WeighSheetModal({
  item,
  unitPrice,
  stockCap,
  initialWeight = '',
  mode = 'add',
  qtyStep,
  minOrderQty,
  maxOrderQty,
  onConfirm,
  onClose,
}) {
  const kgRef = useRef(null);
  const rate = Number(unitPrice ?? item?.price) || 0;
  const maxStock = Number(stockCap ?? item?.stock);
  const hasStockCap = Number.isFinite(maxStock);
  const step = Number(qtyStep ?? item?.qtyStep) || 0.001;
  const minQty = Number(minOrderQty ?? item?.minOrderQty);
  const maxQty = Number(maxOrderQty ?? item?.maxOrderQty);
  const hasMin = Number.isFinite(minQty) && minQty > 0;
  const hasMax = Number.isFinite(maxQty) && maxQty > 0;
  const unit = item?.unit || 'kg';

  const chips = QUICK_WEIGHTS.filter((chip) => qtyOnStep(chip.kg, step));

  const [kgText, setKgText] = useState('');
  const [gramsText, setGramsText] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialWeight !== '' && initialWeight != null) {
      const kg = quantizeKg(initialWeight);
      if (kg != null && kg > 0) {
        setKgText(kg.toFixed(3));
        setGramsText(String(Math.round(kg * 1000)));
      }
    }
    const t = setTimeout(() => kgRef.current?.focus({ preventScroll: true }), 80);
    return () => clearTimeout(t);
  }, [initialWeight]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const weightKg = useMemo(() => parseKgInput(kgText), [kgText]);
  const total = weightKg != null ? lineAmount(rate, weightKg) : 0;

  const validationError = useMemo(() => {
    if (weightKg == null) return '';
    if (hasMin && weightKg < minQty) return `Need at least ${formatWeightKg(minQty, unit)}.`;
    if (hasMax && weightKg > maxQty) return `Cap ${formatWeightKg(maxQty, unit)}.`;
    if (!qtyOnStep(weightKg, step)) return `Use steps of ${stepLabel(step, unit)}.`;
    if (hasStockCap && weightKg > maxStock) return `Only ${formatWeightKg(maxStock, unit)} in stock.`;
    return '';
  }, [weightKg, hasMin, minQty, hasMax, maxQty, step, hasStockCap, maxStock, unit]);

  const canConfirm = weightKg != null && !validationError;

  const applyKgText = (text) => {
    setKgText(text);
    setError('');
    const parsed = parseKgInput(text);
    if (parsed == null) {
      if (!String(text).trim()) setGramsText('');
      return;
    }
    setGramsText(String(Math.round(parsed * 1000)));
  };

  const applyGramsText = (text) => {
    setGramsText(text);
    setError('');
    const cleaned = String(text || '').trim();
    if (!cleaned) {
      setKgText('');
      return;
    }
    const grams = Number(cleaned);
    if (!Number.isFinite(grams) || grams <= 0) return;
    const kg = gramsToKg(grams);
    setKgText(kg > 0 ? kg.toFixed(3) : '');
  };

  const applyQuick = (kg) => {
    setKgText(kg.toFixed(3));
    setGramsText(String(Math.round(kg * 1000)));
    setError('');
  };

  const handleConfirm = () => {
    const qty = parseKgInput(kgText);
    if (qty == null) {
      setError('Enter a weight greater than zero.');
      kgRef.current?.focus();
      return;
    }
    if (validationError) {
      setError(validationError);
      kgRef.current?.focus();
      return;
    }
    onConfirm?.(qty);
  };

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="weigh-sheet-title"
      onMouseDown={onClose}
    >
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>

        <div className={styles.head}>
          <span className={styles.headIcon} aria-hidden>
            <Scale size={18} />
          </span>
          <div>
            <h2 id="weigh-sheet-title" className={styles.title}>
              {mode === 'edit' ? 'Edit weight' : 'Weigh item'}
            </h2>
            <p className={styles.subtitle}>{item?.name}</p>
          </div>
        </div>

        <div className={styles.rateRow}>
          <span className={styles.rateLabel}>Rate</span>
          <span className={styles.rateValue}>{formatCurrency(rate)} / {unit}</span>
        </div>

        <div className={styles.dualInputs}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Weight (kg)</span>
            <div className={styles.weightInput}>
              <input
                ref={kgRef}
                type="number"
                inputMode="decimal"
                min="0"
                step={step}
                placeholder="0.000"
                value={kgText}
                onChange={(e) => applyKgText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleConfirm();
                  }
                }}
              />
              <span className={styles.weightSuffix}>kg</span>
            </div>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Grams</span>
            <div className={styles.weightInput}>
              <input
                type="number"
                inputMode="numeric"
                min="0"
                step="1"
                placeholder="0"
                value={gramsText}
                onChange={(e) => applyGramsText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleConfirm();
                  }
                }}
              />
              <span className={styles.weightSuffix}>g</span>
            </div>
          </label>
        </div>

        {chips.length > 0 && (
          <div className={styles.chips}>
            {chips.map((chip) => (
              <button
                key={chip.kg}
                type="button"
                className={styles.chip}
                onClick={() => applyQuick(chip.kg)}
              >
                {chip.label}
              </button>
            ))}
          </div>
        )}

        <div className={styles.liveTotal}>
          <span>Line total</span>
          <strong>{formatCurrency(total)}</strong>
        </div>
        {hasStockCap && (
          <p className={styles.stockHint}>{formatWeightKg(maxStock, unit)} available</p>
        )}
        {(error || validationError) && <p className={styles.error}>{error || validationError}</p>}

        <div className={styles.footer}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleConfirm} disabled={!canConfirm}>
            {mode === 'edit' ? 'Update cart' : 'Add to cart'}
          </Button>
        </div>
      </div>
    </div>
  );
}
